import i18n from "@helpers/lib/i18n";
import type {
	BackendSessionResponse,
	BaseItemSchema,
	Choice,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import type { Slide } from "@memorang/types/src/session/new-session";

export const orgKey = "ets-toefl-diagnostic";

export const diagnosticTagIds = {
	readingListening: 1000,
	speakingWriting: 1001,
};

export const getIntlExplanationForChoice = (choice: Choice) => {
	switch (i18n.language) {
		case "zh-CN":
			return choice?.explanation_mandarin;
		case "ja-JP":
			return choice?.explanation_japanese;
		default:
			return choice?.explanation;
	}
};
export const findLastUpdatedItemIndex = (
	items: BaseItemSchema[][],
): { lastUpdatedBlockIndex: number; lastUpdatedItemIndex: number } => {
	const lastUpdatedBlockIndex = items.findIndex((block) =>
		block.some((item) => item.lastUpdated),
	);
	// Find the index of the last item that has lastUpdatedAt property using a more functional approach
	const lastUpdatedItemIndex =
		items[lastUpdatedBlockIndex]
			?.map((item, index) => ({ hasUpdate: Boolean(item.lastUpdated), index }))
			?.filter((item) => item.hasUpdate)
			?.at(0)?.index || 0;

	return {
		lastUpdatedBlockIndex:
			lastUpdatedBlockIndex >= 0 ? lastUpdatedBlockIndex : 0,
		lastUpdatedItemIndex:
			lastUpdatedItemIndex >= 0 ? lastUpdatedItemIndex + 1 : 0,
	};
};

const flattenSessionItems = (
	sessionItems: BackendSessionResponse["sessionItems"],
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
): BaseItemSchema[][] => {
	const hasBlocks = sessionItems.some((item) => item.metadata?.section);

	if (hasBlocks) {
		const blocksMap = new Map<string, BaseItemSchema[]>();

		for (const item of sessionItems) {
			const section =
				item.metadata?.section || item.stimulus?.metadata?.section;
			if (section !== undefined) {
				if (item.type === "SET") {
					if (!blocksMap.has(section)) {
						blocksMap.set(section, []);
					}

					const mappedItems = item.items!.map((setItem) => ({
						...setItem,
						stimulus: item.stimulus,
						currentStimulusItemIndex: setItem.currentStimulusItemIndex ?? 0,
						numTotalStimulusItems: item.items!.length,
						blockId: section,
					}));

					blocksMap.get(section)!.push(...mappedItems);
				} else {
					if (!blocksMap.has(section)) {
						blocksMap.set(section, []);
					}
					blocksMap.get(section)!.push(item);
				}
			}
		}

		const sortedSections = Array.from(blocksMap.keys()).sort((a, b) => {
			const aNum = Number(a);
			const bNum = Number(b);
			if (!(Number.isNaN(aNum) || Number.isNaN(bNum))) {
				return aNum - bNum;
			}
			return String(a).localeCompare(String(b));
		});

		const result = sortedSections.map((section) => blocksMap.get(section)!);

		return result;
	}
	const result = sessionItems.flatMap((setOrItem): BaseItemSchema[] => {
		if (setOrItem.type === "SET") {
			return (
				setOrItem.items?.map((item, index) => ({
					...item,
					stimulus: setOrItem.stimulus,
					currentStimulusItemIndex: index + 1,
					numTotalStimulusItems: setOrItem.items?.length,
				})) ?? []
			);
		}
		return [setOrItem];
	});

	return [result];
};

const getSlidesData = (slides?: Slide[]) => {
	return (
		slides
			?.filter((slide) => slide.media?.length && slide.media?.length > 0)
			.map((slide) => ({
				image:
					slide.media?.find(
						(m) =>
							m.type === "image" || m.type === "IMAGE" || m.type === "images",
					)?.url ?? "a",
				audio:
					slide.media?.find((m) => m.type === "audio" || m.type === "AUDIO")
						?.url ?? "",
			})) ?? []
	);
};

const getIsSlidesHasPassage = (slides?: Slide[]) => {
	return slides?.some((slide) => slide?.passage != null);
};

const isMultiSelect = ({
	maxChoiceCount,
	minChoiceCount,
	mappedItemType,
}: {
	maxChoiceCount?: number;
	minChoiceCount?: number;
	mappedItemType?: RenderableItemTypes;
}) => {
	return (
		mappedItemType === "SATA" ||
		(mappedItemType === "MCQ" &&
			Boolean(maxChoiceCount) &&
			Boolean(minChoiceCount))
	);
};

export {
	flattenSessionItems,
	getIsSlidesHasPassage,
	getSlidesData,
	isMultiSelect,
};
